'use client';

import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Icon } from '@/components/ui/Icon';
import { useDebouncedCallback } from '@/hooks/useDebounce';
import { useContactTracking } from '@/hooks/useContactTracking';
import { usePosition } from '@/hooks/usePosition';
import type { StoreHit } from '@/lib/algolia';
import { getCountryFromLocale } from '@/lib/i18n';

import { buildAddressLines, DEBOUNCE_DELAY, fetchBoutiqueStores } from './BoutiquePickup.helpers';
import styles from './BoutiquePickup.module.scss';
import type {
  BoutiqueCardProps,
  BoutiqueHoursRowProps,
  BoutiquePickupProps,
} from './BoutiquePickup.types';
import { WEEKDAYS } from './BoutiquePickup.types';

// ============================================
// Sub-components
// ============================================

const BoutiqueHoursRow = ({ day, hours }: BoutiqueHoursRowProps) => {
  const t = useTranslations('checkout');
  const dayHours = hours[day];
  const interval = dayHours?.openIntervals?.[0];

  return (
    <div className={styles.hoursRow}>
      <span className={styles.hoursDay}>
        {t(`delivery.boutique.days.${day}` as Parameters<typeof t>[0])}
      </span>
      <span>{interval ? `${interval.start} - ${interval.end}` : '–'}</span>
    </div>
  );
};

const BoutiqueCard = ({ store, selected, onSelect, lang }: BoutiqueCardProps) => {
  const t = useTranslations('checkout');
  const { trackPhoneContact } = useContactTracking();
  const countryCode = getCountryFromLocale(lang);
  const isJapan = countryCode.toUpperCase() === 'JP';

  const name = store.name_en || store.name;
  const addressLines = buildAddressLines(store, isJapan);

  const hours = store.hours as
    | Record<string, { openIntervals: Array<{ start: string; end: string }> }>
    | undefined;

  return (
    <div className={styles.cardWrapper}>
      <label
        className={clsx(styles.card, { [styles.cardSelected]: selected })}
        aria-checked={selected}
      >
        <input
          type="radio"
          name="boutiquePickup"
          value={store.store_id}
          checked={selected}
          onChange={onSelect}
          className={styles.cardRadio}
        />
        <div className={styles.cardBody}>
          <p className={styles.storeName}>{name}</p>

          {addressLines.map((line, index) => (
            <p key={index} className={styles.storeAddress}>
              {line}
            </p>
          ))}

          {store.phone && (
            <p className={styles.storePhone}>
              {t('delivery.boutique.phone')}{' '}
              <a href={`tel:${store.phone}`} onClick={trackPhoneContact}>
                {store.phone}
              </a>
            </p>
          )}

          {hours && (
            <div className={styles.hoursSection}>
              <p className={styles.hoursTitle}>{t('delivery.boutique.hours')}</p>
              <div className={styles.hoursTable}>
                {WEEKDAYS.map((day) => (
                  <BoutiqueHoursRow key={day} day={day} hours={hours} />
                ))}
              </div>
            </div>
          )}
        </div>
      </label>
    </div>
  );
};

// ============================================
// Main Component
// ============================================

export const BoutiquePickup = ({ lang, value, onChange, error }: BoutiquePickupProps) => {
  const t = useTranslations('checkout');
  const countryCode = getCountryFromLocale(lang);

  const { position, error: positionError } = usePosition();
  const aroundLatLng = position
    ? `${position.coords.latitude},${position.coords.longitude}`
    : undefined;

  const [keyword, setKeyword] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [boutiqueList, setBoutiqueList] = useState<StoreHit[]>([]);
  const [totalHits, setTotalHits] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const hasFetched = useRef(false);

  const fetchBoutiques = useCallback(
    async (query: string, page: number) => {
      setIsLoading(true);

      const result = await fetchBoutiqueStores({
        countryCode,
        query,
        page,
        aroundLatLng,
      });

      setIsLoading(false);

      if (!result) return;

      setTotalHits(result.totalHits);

      if (page === 0) {
        setBoutiqueList(result.stores);
      } else {
        setBoutiqueList((prev) => [...prev, ...result.stores]);
      }
    },
    [countryCode, aroundLatLng]
  );

  useEffect(() => {
    if (position || positionError) {
      if (!hasFetched.current || keyword || currentPage > 0) {
        hasFetched.current = true;
        fetchBoutiques(keyword, currentPage);
      }
    }
  }, [currentPage, keyword, position, positionError, fetchBoutiques]);

  const handleLoadMore = () => {
    setCurrentPage((prev) => prev + 1);
  };

  const handleSearchChange = useDebouncedCallback((searchValue: string) => {
    setKeyword(searchValue);
    setCurrentPage(0);
    hasFetched.current = false;
  }, DEBOUNCE_DELAY);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  return (
    <div className={styles.container}>
      {/* Search */}
      <div className={styles.searchSection}>
        <span className={styles.searchLabel}>{t('delivery.boutique.findClosest')}</span>
        <div className={styles.searchInputWrapper}>
          <Icon name="location" size={20} className={styles.searchIcon} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder={t('delivery.boutique.searchPlaceholder')}
            onChange={(e) => handleSearchChange(e.target.value)}
            onKeyDown={handleKeyDown}
            aria-label={t('delivery.boutique.findClosest')}
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <span className={styles.error} role="alert">
          {error}
        </span>
      )}

      {/* Boutique list */}
      <div
        className={styles.boutiqueList}
        role="radiogroup"
        aria-label={t('delivery.boutique.selectBoutique')}
        aria-invalid={error ? true : undefined}
      >
        {boutiqueList.map((store) => (
          <BoutiqueCard
            key={store.objectID}
            store={store}
            selected={value === store.store_id}
            onSelect={() => onChange(store.store_id, store)}
            lang={lang}
          />
        ))}

        {totalHits > boutiqueList.length && (
          <div className={styles.loadMore}>
            <button
              type="button"
              className={styles.loadMoreButton}
              onClick={handleLoadMore}
              disabled={isLoading}
            >
              {t('delivery.boutique.seeMore')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

BoutiquePickup.displayName = 'BoutiquePickup';
